/**
 * This file started off from the @react-aria's `useSelectState` implementation
 * which can be found here
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-stately/select/src/useSelectState.ts
 *
 * At the time of writing, @react-aria did not directly support select or
 * combobox with multiple selection. The reason being that there is no such
 * ARIA pattern and some interactions of a multiselect can't be translated to a
 * native <select> which is what `useSelect` tries to emulate.
 *
 * Some relevant discussions can be found here:
 * https://github.com/adobe/react-spectrum/issues/2140
 * https://github.com/adobe/react-spectrum/discussions/2218
 */

import { SingleSelection } from '@react-types/shared';
import {
  MenuTriggerState,
  useMenuTriggerState,
} from 'react-stately/useMenuTriggerState';
import { SelectProps } from '@react-types/select';
import { useState } from 'react';
import {
  MultiSelectListProps,
  MultiSelectListState,
  useMultiSelectListState,
} from './useMultiSelectListState';

// Omit all single-selection related props from SelectProps and add all
// multi-selection related props.
export type MultiSelectStateOptions<T> = Omit<
  SelectProps<T>,
  keyof SingleSelection | 'children'
> &
  MultiSelectListProps<T>;

export type MultiSelectState<T> = MultiSelectListState<T> &
  MenuTriggerState & {
    /** Whether the select is currently focused. */
    readonly isFocused: boolean;

    /** Sets whether the select is focused. */
    setFocused(isFocused: boolean): void;
  };

/**
 * Provides state management for a multiselect component. Handles building a
 * collection of items from props, handles the open state for the popup menu,
 * and manages multiple selection state.
 */
export function useMultiSelectState<T extends object>(
  props: MultiSelectStateOptions<T>
): MultiSelectState<T> {
  const triggerState = useMenuTriggerState(props);
  const listState = useMultiSelectListState({
    ...props,
    onSelectionChange: (selection) => {
      if (props.onSelectionChange != null) {
        props.onSelectionChange(selection);
      }
    },
  });

  const [isFocused, setFocused] = useState(false);

  return {
    ...listState,
    ...triggerState,
    close() {
      triggerState.close();
    },
    open() {
      // Don't open if the collection is empty.
      if (listState.collection.size !== 0) {
        triggerState.open();
      }
    },
    toggle(focusStrategy) {
      if (listState.collection.size !== 0) {
        triggerState.toggle(focusStrategy);
      }
    },
    isFocused,
    setFocused,
  };
}
